/**
 * nap-frontend browser telemetry logger — Phase 1 (logging only, ZERO new deps).
 *
 * Captures structured logs in the browser and ships them to the backend ingest
 * endpoint (POST {BACKEND_URL}/telemetry/logs). The backend enriches and forwards
 * them to the observability pipeline. No OpenTelemetry packages are imported here;
 * trace correlation is wired in FE Phase 2 via `setTraceContextProvider`.
 *
 * Lifecycle (call once from src/index.tsx, after the backend URL is resolved):
 *   import { initTelemetryLogger, installGlobalErrorHandlers, log } from 'telemetry/logger';
 *   initTelemetryLogger({ enabled, endpoint, environment, version });
 *   installGlobalErrorHandlers();
 *
 * Everything is a no-op until initialised with `enabled: true`, so this is safe to
 * import unconditionally and is fully controlled by runtime config (no rebuild).
 */

import { resolveTelemetryAuth } from './authToken';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogRecord {
  level: LogLevel;
  msg: string;
  ts: string;
  /** populated at log time in Phase 2 once the OTel Web SDK is present */
  trace_id?: string;
  span_id?: string;
  attrs?: Record<string, unknown>;
}

interface LoggerConfig {
  enabled: boolean;
  /** full URL, e.g. https://api.norscom-dev.norscom.cloud/telemetry/logs */
  endpoint: string;
  environment?: string;
  version?: string;
  /**
   * Capture console.log/info/debug too (noisy/PII risk). Default false.
   *
   * NOTE: this only gets you the `console.info` half by default. `console.log`
   * is captured at `debug`, which `minLevel` drops unless the floor is lowered
   * — and lowering it re-admits the redux-logger state dumps that floor exists
   * to stop, because redux-logger writes through `console.log`. The two knobs
   * are effectively mutually exclusive; that is deliberate, not an oversight.
   * Remapping `console.log` to `info` would not separate them, since both
   * travel the same channel.
   */
  captureVerboseConsole?: boolean;
  /**
   * Minimum level actually SHIPPED to the backend. Defaults to 'info'.
   *
   * `debug` records are still written to the browser console — they are simply
   * not sent. This is what stops redux-logger `%c prev state` dumps (8 KB of
   * Redux store per action, including the user profile) from reaching Loki;
   * see issue #1558. The backend is expected to enforce its own floor too, so
   * that a cached old bundle cannot bypass this — but that is the backend's
   * invariant to state, not ours; this gate must stand on its own.
   *
   * See `captureVerboseConsole` for why raising verbosity and lowering this
   * floor are not independent knobs.
   */
  minLevel?: LogLevel;
  /**
   * App-level ingest token (RUM). Used as the Authorization fallback BEFORE
   * login, when there is no user JWT, so pre-login browser errors still reach
   * the backend (which maps this token to authenticated=false + default tenant).
   * A user JWT always takes precedence. Delivered via runtime /config.json.
   */
  ingestToken?: string;
}

const BATCH_MAX = 50;
const FLUSH_MS = 5000;
const MSG_MAX = 8192;
const ATTR_VALUE_MAX = 1024;
const ATTR_KEY_MAX = 32;
const MAX_DEPTH = 4;
const REDACTED = '[REDACTED]';
const TRUNCATED = '[TRUNCATED]';
// Secret- AND PII-looking keys. Beyond credentials this covers the user profile
// blobs a redux-logger state dump drags along: the sample in issue #1558 carried
// the admin's bcrypt hash, mail address and full user_settings object to the sink.
//
// Two deliberate sharp edges, so they read as choices rather than accidents:
//   - the alternatives are UNANCHORED, so `mail` also drops `mailbox`,
//     `mail_server_host`, `emailTemplate`. Over-matching is the right direction
//     for a privacy net; no attr key the app emits today collides.
//   - matching keys are DELETED, not replaced with [REDACTED], so a dropped
//     diagnostic attribute leaves no marker. Accepted: a marker on every
//     profile key in a state dump would be noise.
const SECRET_KEY_RE =
  /authorization|passwd|password|token|secret|cookie|api[-_]?key|private[-_]?key|public[-_]?key|credential|mail|user[-_]?settings|user[-_]?config|session[-_]?key/i;
// Best-effort scan for obvious secret VALUES (bcrypt/argon2 hashes, JWTs, Bearer
// tokens) so a secret passed as a bare string — not under a secret-looking key —
// is still redacted. Case-INSENSITIVE and with no length floor on the token:
// `fetch` and most axios interceptors emit a lowercase `bearer <token>`, and a
// short credential is still a credential. Keep in step with the backend's
// modules/telemetry/redaction.js — the two lists are duplicated across packages.
// The `eyJ` net is ONE deliberately loose branch: any `eyJ...` blob is redacted,
// not just a structural three-segment JWT. That is what catches opaque refresh
// tokens, JWEs and unsigned two-segment tokens, and `[\w.+/=-]` also covers
// standard-alphabet base64.
//
// Do NOT re-add a structural `eyJ<seg>.<seg>.<seg>` alternative ahead of this
// one. Alternation is ordered, so it would match first at the `eyJ` position and
// its `[\w-]` segment class stops at the first `+`, `/` or fourth `.` — shipping
// the tail of a JWE's ciphertext and tag, or a standard-base64 signature, in the
// clear. It also catches nothing this branch misses: every structural match is
// >=18 trailing characters drawn from `[\w.+/=-]`, so this is a strict superset.
const SECRET_VALUE_RE = /\$2[abxy]?\$\d{2}\$[./A-Za-z0-9]{20,}|\$argon2[a-z]*\$[^\s"']+|Bearer\s+[\w.+/=-]+|eyJ[\w.+/=-]{10,}/gi;

/** Severity ordering for the minimum-shipped-level gate. */
const LEVEL_ORDER: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };
const DEFAULT_MIN_LEVEL: LogLevel = 'info';

/** Per-tab session id (in-memory only; no localStorage per artifact storage rules). */
export const SESSION_ID: string = safeUuid();

let cfg: LoggerConfig | null = null;
let queue: LogRecord[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let consolePatched = false;
let handlersInstalled = false;

/** Optional hook so Phase 2 can attach the active trace context per record. */
let traceContextProvider: (() => { trace_id?: string; span_id?: string }) | null = null;
export function setTraceContextProvider(fn: () => { trace_id?: string; span_id?: string }): void {
  traceContextProvider = fn;
}

/**
 * Optional hook returning the current app JWT. When set, the ingest request
 * carries `Authorization: Bearer <jwt>` so the backend can authenticate the
 * browser and derive its tenant. Wired in index.tsx to the app token store.
 */
let authTokenProvider: (() => string | undefined) | null = null;
export function setAuthTokenProvider(fn: () => string | undefined): void {
  authTokenProvider = fn;
}

export function initTelemetryLogger(config: LoggerConfig): void {
  cfg = config;
  if (!config.enabled || !config.endpoint) return;
  if (!timer) {
    timer = setInterval(() => flush(false), FLUSH_MS);
    // Best-effort flush when the tab is hidden or unloaded.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
  }
}

/** Is logging active? */
function active(): boolean {
  return !!cfg && cfg.enabled && !!cfg.endpoint;
}

/**
 * Would a record at this level be shipped?
 *
 * Fails CLOSED. An unrecognised `minLevel` yields `undefined` from LEVEL_ORDER,
 * and the original `if (LEVEL_ORDER[level] < floor) return;` then evaluated
 * `n < undefined` as false and shipped EVERYTHING including debug — the exact
 * opposite of what a privacy gate should do when misconfigured.
 *
 * Two things keep it closed now, deliberately: the predicate is phrased as
 * `>=`, so an undefined floor yields false and the record is dropped; and the
 * `??` pins the floor back to the default so the behaviour survives someone
 * reformulating the comparison. `backendUrl.ts` normalises the value today, so
 * this is defence in depth rather than a live path.
 *
 * Hoisted out of `enqueue` so the console wrapper can consult it BEFORE
 * stringifying its arguments — otherwise a redux-logger `console.log`
 * JSON.stringify's the whole store on every dispatch just to throw it away.
 */
function shipped(level: LogLevel): boolean {
  const floor = LEVEL_ORDER[cfg?.minLevel ?? DEFAULT_MIN_LEVEL] ?? LEVEL_ORDER[DEFAULT_MIN_LEVEL];
  return LEVEL_ORDER[level] >= floor;
}

function enqueue(level: LogLevel, msg: string, attrs?: Record<string, unknown>): void {
  if (!active()) return;
  // Drop below-threshold records before they are queued. The console wrapper has
  // already called through to the native console, so `debug` output is still
  // visible in devtools — it just is not shipped (issue #1558).
  if (!shipped(level)) return;
  const provider = traceContextProvider;
  const tc = provider ? safe(() => provider()) : undefined;
  const enriched = enrich(attrs);
  // Trace context precedence: the active span (provider) wins; otherwise fall
  // back to ids supplied explicitly via attrs (e.g. a failed request whose span
  // is no longer the active context). Promote them to top-level and strip the
  // duplicates so they don't also surface as nap_trace_id/nap_span_id.
  const attrTraceId = typeof enriched.trace_id === 'string' ? (enriched.trace_id as string) : undefined;
  const attrSpanId = typeof enriched.span_id === 'string' ? (enriched.span_id as string) : undefined;
  delete enriched.trace_id;
  delete enriched.span_id;
  const rec: LogRecord = {
    level,
    msg: truncate(redactSecrets(String(msg)), MSG_MAX),
    ts: new Date().toISOString(),
    trace_id: tc?.trace_id ?? attrTraceId,
    span_id: tc?.span_id ?? attrSpanId,
    attrs: enriched,
  };
  queue.push(rec);
  if (queue.length >= BATCH_MAX) flush(false);
}

/**
 * Per-record attributes. Service identity (service.name / service.version /
 * deployment.environment) is deliberately NOT here: it describes the emitting
 * RESOURCE, not the event, and is sent once per batch in the payload's `resource`
 * block instead. Putting it here is what produced the unqueryable `nap_service.name`
 * body field in issue #1558.
 */
function enrich(attrs?: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {
    route: safe(() => window.location?.pathname),
  };
  return { ...out, ...sanitizeAttrs(attrs) };
}

function flush(useBeacon: boolean): void {
  // `active()` guarantees cfg is set; the explicit `!cfg` check also narrows the
  // type here so we don't need a non-null assertion below.
  if (!active() || !cfg || queue.length === 0) return;
  const batch = queue;
  queue = [];
  // `resource` carries the emitting service's identity once per batch, because it
  // describes the emitter rather than any single event — putting it in per-record
  // attrs is what produced the unqueryable `nap_service.name` body field (#1558).
  //
  // We send service.name and deployment.environment for completeness while
  // EXPECTING the server to assert its own: both are indexed Loki stream labels,
  // so a client-settable value would be an index-cardinality attack. Describing
  // the backend's mechanism here would go stale silently — this side cannot see
  // when that changes — so this states only why the frontend sends what it does.
  const payload = JSON.stringify({
    session_id: SESSION_ID,
    resource: {
      'service.name': 'nap-frontend',
      // Explicit sentinel rather than omitting the key: JSON.stringify drops an
      // undefined value, and the backend treats an absent override as "use my
      // own version" — which is the browser-logs-report-the-backend's-version
      // confusion this change exists to remove.
      'service.version': cfg.version ?? 'unknown',
      'deployment.environment': cfg.environment,
    },
    logs: batch,
  });
  const url = cfg.endpoint;
  const tokenProvider = authTokenProvider;
  const token = tokenProvider ? safe(() => tokenProvider()) : undefined;
  // User JWT when it is usable, app-level ingest token otherwise. Either lets the
  // backend accept the batch (the ingest token only ever as default tenant).
  // See authToken.ts for why a blank or expired JWT must NOT win here.
  const auth = resolveTelemetryAuth(token, cfg.ingestToken);

  // sendBeacon survives page unload but cannot set an Authorization header, so it
  // is only used at unload time AND only when there is nothing to authenticate
  // with (no usable user JWT and no ingest token); otherwise we fall through to
  // fetch so the Authorization header is actually sent.
  //
  // Giving up the beacon whenever we have a credential is deliberate: with
  // NAP_TELEMETRY_REQUIRE_AUTH=true (every deployed environment) a headerless
  // beacon is 401'd and the batch is lost, so "beacon before login" would trade a
  // request that arrives for one that is guaranteed to be discarded. The cost is
  // the shared ~64KB keepalive budget noted below, which the post-login path
  // already paid.
  if (useBeacon && !auth && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    } catch {
      /* fall through to fetch */
    }
  }
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (auth) headers.Authorization = `Bearer ${auth}`;
  // Never send cookies; never let a telemetry failure surface to the app.
  // keepalive only on the unload path: the browser caps total keepalive body
  // size (~64KB shared across in-flight requests), so a large interval batch
  // could be silently dropped — interval flushes don't need it.
  void fetch(url, {
    method: 'POST',
    headers,
    body: payload,
    keepalive: useBeacon,
    credentials: 'omit',
  }).catch(() => {
    /* drop on failure — telemetry must never break the UI */
  });
}

function serializeError(err: unknown): Record<string, unknown> {
  if (err instanceof Error) {
    return {
      error_name: err.name,
      error_message: err.message,
      error_stack: truncate(err.stack || '', MSG_MAX),
    };
  }
  if (err === undefined || err === null) return {};
  return { error_message: truncate(String(err), ATTR_VALUE_MAX) };
}

/** Public structured-logging API used across the app. */
export const log = {
  debug: (msg: string, attrs?: Record<string, unknown>) => enqueue('debug', msg, attrs),
  info: (msg: string, attrs?: Record<string, unknown>) => enqueue('info', msg, attrs),
  warn: (msg: string, attrs?: Record<string, unknown>) => enqueue('warn', msg, attrs),
  error: (msg: string, attrs?: Record<string, unknown>, err?: unknown) => enqueue('error', msg, { ...attrs, ...(err ? serializeError(err) : {}) }),
};

/**
 * Capture uncaught errors, unhandled rejections, and console output.
 * Idempotent (safe under React 18 StrictMode double-invocation).
 */
export function installGlobalErrorHandlers(): void {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (e: ErrorEvent) => {
    log.error('window.onerror', { filename: e.filename, lineno: e.lineno, colno: e.colno, source: 'window.error' }, e.error ?? e.message);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    log.error('unhandledrejection', { source: 'unhandledrejection' }, e.reason);
  });

  patchConsole();
}

/** Wrap console so error/warn (and optionally the rest) are also forwarded. */
function patchConsole(): void {
  if (consolePatched || typeof console === 'undefined') return;
  consolePatched = true;

  const wrap = (method: 'log' | 'info' | 'warn' | 'error', level: LogLevel) => {
    const original = console[method]?.bind(console) ?? (() => undefined);
    console[method] = (...args: unknown[]) => {
      original(...args); // keep native console behaviour
      // Before stringifyArg, not after: `enqueue` would drop the record anyway,
      // but only after this wrapper had serialised every argument. With
      // captureVerboseConsole that is the entire Redux store, per dispatch.
      if (!active() || !shipped(level)) return;
      try {
        const errArg = args.find((a) => a instanceof Error);
        const msg = args
          .filter((a) => !(a instanceof Error))
          .map(stringifyArg)
          .join(' ');
        enqueue(level, msg || (errArg instanceof Error ? errArg.message : ''), {
          source: `console.${method}`,
          ...(errArg ? serializeError(errArg) : {}),
        });
      } catch {
        /* never let capture break console */
      }
    };
  };

  wrap('error', 'error');
  wrap('warn', 'warn');
  if (cfg?.captureVerboseConsole) {
    wrap('info', 'info');
    wrap('log', 'debug');
  }
}

// ----------------------------- helpers -----------------------------

/**
 * Redact secrets from log attributes. Best-effort but defence-in-depth:
 *   - drops keys matching SECRET_KEY_RE at EVERY nesting level (not just top),
 *   - scans string values for obvious secret patterns (JWTs, Bearer tokens),
 *   - the same scan is applied to `msg` at enqueue time.
 * It is heuristic, not a guarantee — unusual secret shapes can still slip
 * through, so callers should avoid logging secrets in the first place.
 */
function sanitizeAttrs(attrs?: Record<string, unknown>): Record<string, unknown> {
  if (!attrs || typeof attrs !== 'object') return {};
  const out: Record<string, unknown> = {};
  let n = 0;
  for (const [k, v] of Object.entries(attrs)) {
    if (n++ >= ATTR_KEY_MAX) break;
    if (SECRET_KEY_RE.test(k)) continue; // never forward secret-named keys
    // error_stack keeps the larger MSG_MAX budget (set by serializeError) so
    // stacks stay useful; other string values are capped tighter.
    const cap = k === 'error_stack' ? MSG_MAX : ATTR_VALUE_MAX;
    if (typeof v === 'string') out[k] = truncate(redactSecrets(v), cap);
    else if (typeof v === 'number' || typeof v === 'boolean' || v === null) out[k] = v;
    // Recurse into nested objects/arrays to strip secret-named keys before
    // stringifying, then scan the result for secret values as a final net.
    else out[k] = truncate(redactSecrets(stringifyArg(sanitizeDeep(v, 1))), cap);
  }
  return out;
}

/** Replace obvious secret substrings (JWTs, Bearer tokens) with [REDACTED]. */
function redactSecrets(s: string): string {
  return s.replace(SECRET_VALUE_RE, REDACTED);
}

/**
 * Recursively drop secret-named keys from nested objects/arrays. Depth-capped.
 *
 * Past the cap an object is replaced wholesale rather than returned as-is.
 * Returning it raw meant its keys were never tested, so a plaintext secret
 * deeper than MAX_DEPTH was stringified straight into the attribute — and the
 * value scan is no help there, since it matches hashes and tokens, not
 * plaintext. A Redux store dump is exactly this deep
 * (`state.users.userData.profile.credentials.password`).
 *
 * Scalars past the cap are still returned: a leaf hides no keys, and the value
 * scan still runs over the stringified result.
 */
function sanitizeDeep(v: unknown, depth: number): unknown {
  if (v === null || typeof v !== 'object') return v;
  if (depth > MAX_DEPTH) return TRUNCATED;
  if (Array.isArray(v)) return v.map((item) => sanitizeDeep(item, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v)) {
    if (SECRET_KEY_RE.test(k)) continue;
    out[k] = sanitizeDeep(val, depth + 1);
  }
  return out;
}

function stringifyArg(a: unknown): string {
  if (typeof a === 'string') return a;
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function safeUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
